import {
  Badge,
  Box,
  Collapse,
  Fab,
  IconButton,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  IconAlertCircle,
  IconBell,
  IconChevronDown,
  IconChevronUp,
  IconX
} from "@tabler/icons";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useFileJobs } from "src/hooks/useFileJobs";
import JobItem from "./JobItem";

const FloatingJobPanel = () => {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [hasNewJobs, setHasNewJobs] = useState(false);
  const [jobs, setJobs] = useState([]);
const [lastAutoOpenedJobId, setLastAutoOpenedJobId] = useState(null);

  const hasJobPanelAccess = useMemo(() => {
    try {
      const store = localStorage.getItem("store");
      if (store) {
        const user = JSON.parse(store)?.user;
        const perms = user?.permissions || [];
        const isSuperAdmin = user?.role === "super_admin";
        return isSuperAdmin || perms.includes("view_reports") || perms.includes("create_reports");
      }
    } catch (e) {
      console.error("Error checking permissions:", e);
    }
    return false;
  }, []);

  const { recentJobs, newJobs, jobUpdates, deleteJob, showDisconnectAlert } = useFileJobs();

  // Optimized Auto-show logic
  useEffect(() => {
    if (newJobs.length > 0) {
      const latestJob = newJobs[0]; // Assuming newest is first
      
      // Only auto-open if this specific job hasn't triggered an open yet
      if (latestJob.jobId !== lastAutoOpenedJobId) {
        if (hasJobPanelAccess) {
          setHasNewJobs(true);
          setIsOpen(true);
          setIsMinimized(false);
        } else {
          toast.info(`Download job started: ${latestJob.fileName || latestJob.jobId}`);
        }
        setLastAutoOpenedJobId(latestJob.jobId);
      }
    }
  }, [newJobs, lastAutoOpenedJobId, hasJobPanelAccess]); 

  // Clear badge when panel is opened
  useEffect(() => {
    if (isOpen) {
      setHasNewJobs(false);
    }
  }, [isOpen]);

  // Update jobs when socket updates are received
  useEffect(() => {
    if (Object.keys(jobUpdates).length > 0) {
      setJobs((prevJobs) =>
        prevJobs.map((job) =>
          jobUpdates[job.jobId] ? { ...job, ...jobUpdates[job.jobId] } : job
        )
      );
    }
  }, [jobUpdates]);

  // Add delete handler
  const handleDelete = async (jobId) => {
    try {
      await deleteJob(jobId);
      // Remove from local state immediately for better UX
      setJobs(prev => prev.filter(job => job.jobId !== jobId));
      toast.success('Job deleted successfully');
    } catch (error) {
      toast.error('Failed to delete job');
    }
  };

  // Initialize and update jobs list from recentJobs
  useEffect(() => {
    setJobs(recentJobs);
  }, [recentJobs]);

  // Show only recent jobs (last 5)
  const displayJobs = jobs.slice(0, 5);

  const activeJobsCount = displayJobs.filter(
    (job) => job.status === "processing"
  ).length;

  if (!hasJobPanelAccess) {
    return null;
  }

  return (
    
    <Box
      sx={{
        position: "fixed",
        bottom: 20,
        right: 20,
        zIndex: 1300,
      }}
    >
      {/* Floating Action Button */}
      <Badge
        color="error"
        variant="dot"
        invisible={!hasNewJobs}
        sx={{
          display: "flex",
          justifyContent: "right",
          "& .MuiBadge-dot": {
            width: 12,
            height: 12,
            borderRadius: "50%",
          },
        }}
      >
        <Fab
          color="primary"
          onClick={() => {
            setIsOpen(!isOpen);
            if (!isOpen) {
              setIsMinimized(false);
            }
          }}
          sx={{
            boxShadow: theme.shadows[8],
            "&:hover": {
              boxShadow: theme.shadows[12],
            },
          }}
        >
          <IconBell />
        </Fab>
      </Badge>

      {/* Collapsible Panel */}
      <Collapse in={isOpen}>
        <Paper
          elevation={12}
          sx={{
            mt: 2,
            width: 380,
            maxHeight: 600,
            overflow: "hidden",
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 2,
              borderBottom: 1,
              borderColor: "divider",
              bgcolor: theme.palette.background.paper,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="h6" fontWeight={600}>
                File Jobs
              </Typography>
              {activeJobsCount > 0 && (
                <Box
                  sx={{
                    bgcolor: theme.palette.info.main,
                    color: "white",
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: "0.75rem",
                    fontWeight: 500,
                  }}
                >
                  {activeJobsCount} active
                </Box>
              )}
            </Box>
            <Stack direction="row" spacing={0.5}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsMinimized(!isMinimized);
                }}
                sx={{ color: theme.palette.text.secondary }}
              >
                {isMinimized ? <IconChevronDown /> : <IconChevronUp />}
              </IconButton>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen(false);
                  setIsMinimized(false);
                  setHasNewJobs(false);
                }}
                sx={{ color: theme.palette.text.secondary }}
              >
                <IconX />
              </IconButton>
            </Stack>
          </Box>

          {/* Socket Disconnection Alert */}
          {showDisconnectAlert && (
            <Box sx={{
              p: 1,
              bgcolor: theme.palette.warning.light,
              borderBottom: `1px solid ${theme.palette.warning.main}`,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <IconAlertCircle size={16} color={theme.palette.warning.main} />
              <Typography variant="caption" color="warning.main" fontWeight={500}>
                Real-time updates disconnected. Reconnecting...
              </Typography>
            </Box>
          )}

          {/* Job List */}
          <Collapse in={!isMinimized}>
            <Box
              sx={{
                maxHeight: showDisconnectAlert ? 450 : 500,
                overflow: "auto",
                p: 1,
              }}
            >
              {displayJobs.length === 0 ? (
                <Box
                  sx={{
                    p: 4,
                    textAlign: "center",
                    color: theme.palette.text.secondary,
                  }}
                >
                  <IconBell size={48} style={{ opacity: 0.5, marginBottom: 16 }} />
                  <Typography variant="body2">
                    No recent jobs
                  </Typography>
                  <Typography variant="caption" sx={{ mt: 1 }}>
                    Jobs will appear here when you create them
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={1}>
                  {displayJobs.map((job) => (
                    <JobItem key={job.jobId} job={job} onDelete={handleDelete} />
                  ))}
                </Stack>
              )}
            </Box>
          </Collapse>
        </Paper>
      </Collapse>
    </Box>
  );
};

export default FloatingJobPanel;
